import { format } from 'date-fns';
import React from 'react';

import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { CreatedDaily, CreatedHour, IResDashboard } from '@/types/parcel/dashboard';

import { useDate } from '../Context/DateContext';

interface StateData {
  data: CreatedHour | CreatedDaily | null;
  loading: boolean;
  error: any;
}

const useShipmentStatistics = () => {
  const { selectedDate, dateType } = useDate();
  const [data, setData] = React.useState<StateData>({
    data: null,
    loading: true,
    error: null,
  });

  const chartData = React.useMemo(() => {
    if (!data.data) return [];
    return processData(data.data);
  }, [data.data]);

  const getCreatedByHour = async () => {
    const http = initializeHttpProvider();
    try {
      const response = await http.dashboard.createdHour(selectedDate);
      setData({ data: (response as IResDashboard<CreatedHour>).data, loading: false, error: null });
    } catch (error) {
      setData({ data: null, loading: false, error });
    }
  };

  const getCreatedByDay = async () => {
    const http = initializeHttpProvider();
    try {
      const response = await http.dashboard.createdDaily(selectedDate);
      setData({
        data: (response as IResDashboard<CreatedDaily>).data,
        loading: false,
        error: null,
      });
    } catch (error) {
      setData({ data: null, loading: false, error });
    }
  };

  React.useEffect(() => {
    if (['last48hours', 'today'].includes(dateType)) {
      getCreatedByHour();
    } else {
      getCreatedByDay();
    }
  }, [selectedDate, dateType]);

  return {
    chartData,
    loading: data.loading,
    error: data.error,
  };
};

export default useShipmentStatistics;

interface ChartData {
  name: string;
  currentPeriod: number;
  previousPeriod: number;
}

function processData(data: CreatedHour | CreatedDaily): ChartData[] {
  const dateMap: { [key: string]: ChartData } = {};

  // Procesar currentPeriod
  data.currentPeriod.forEach((period) => {
    const date = 'startDate' in period ? period.startDate : period.date;
    const normalizedDate = format(new Date(date), 'yyyy-MM-dd');
    if (!dateMap[normalizedDate]) {
      dateMap[normalizedDate] = {
        name: normalizedDate,
        currentPeriod: 0,
        previousPeriod: 0,
      };
    }
    dateMap[normalizedDate].currentPeriod += period.count;
  });

  // Procesar previousPeriod
  data.previousPeriod.forEach((period) => {
    const date = 'startDate' in period ? period.startDate : period.date;

    const normalizedDate = format(new Date(date), 'yyyy-MM-dd');

    if (!dateMap[normalizedDate]) {
      dateMap[normalizedDate] = {
        name: normalizedDate,

        currentPeriod: 0,
        previousPeriod: 0,
      };
    }
    dateMap[normalizedDate].previousPeriod += period.count;
  });

  return Object.values(dateMap).sort(
    (a, b) => new Date(a.name).getTime() - new Date(b.name).getTime(),
  );
}
